import React from 'react';
import styles from 'views/MockServices/Tabs/MockTemplates/MockTemplatesListBlazeTable.module.css';
import ServicePicker from 'components/ui/servicePicker/ServicePicker';
import { ApplicationState } from 'store/rootReducer';
import {
  bulkDeleteTemplates,
  createBlankMockServiceTemplate,
  createMockServiceFromTemplate,
  deleteMockServiceTemplate,
  expandTemplate,
  getMockServiceTemplates,
  saveMockServiceTemplate,
  updateMockServiceTemplate,
} from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import { connect } from 'react-redux';
import { confirm } from 'components/ui/modal/BlazeConfirmationModal';
import _ from 'lodash';
import Button from 'react-bootstrap/Button';
import SimpleLoader from 'components/loaders/SimpleLoader';
import HelpTooltip from 'components/overlays/helpTooltip/HelpTooltip';
import { withTranslation, WithTranslation } from 'react-i18next';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BlazeTableActions from 'components/blazeTable/BlazeTableActions';
import MockTemplateDetail from 'views/MockServices/Tabs/MockTemplates/MockTemplateDetail';
import BzFormControl from 'components/forms/BzFormControl';
import { IToast, ToastType } from 'store/ui/toasts/toastsTypes';
import { hideToast, showToastNotification } from 'store/ui/toasts/toastsActions';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import { applySearchTermFilterToList } from 'utils/filterUtils';
import DraftIcon from 'components/ui/icons/DraftIcon';
import { fetchServices } from 'store/services/servicesActions';
import { resetTemplateDetailView } from 'store/mockServiceTemplates/mockServiceTemplateDetail/mockServiceTemplateDetailActions';
import ExpandLink from 'components/blazeTable/ExpandLink';
import { Service, Transaction } from 'types/rest/assetCatalogTypes';
import BzmIcon from 'components/icons/BzmIcon';
import ElevioTooltip from 'components/overlays/helpTooltip/TitleHelpTooltip/ElevioTooltip';
import { getConfigurationsListAction } from 'store/configurations/configurationsActions';
import { getSelectedCountTitle } from 'utils/miscUtil';

type AllProps = StateProps & DispatchProps & WithTranslation;

type LocalState = {
  selectedTemplates: MockServiceTemplate[];
};

class MockTemplatesListBlazeTable extends React.Component<AllProps, LocalState> {
  tableRef: any = React.createRef<BlazeTable>();

  state: LocalState = {
    selectedTemplates: [],
  };

  componentDidMount() {
    this.props.getMockServiceTemplates();
    this.props.getConfigurationsListAction();
  }

  getTable = (): BlazeTable => {
    return this.tableRef as BlazeTable;
  };

  goToFirstPage = () => {
    if (this.tableRef) {
      this.tableRef.pageSelectedHandler(1);
    }
  };

  serviceChanged = (template: MockServiceTemplate, vs: Service | any) => {
    // Do nothing if service id does not change
    if (!vs || template.serviceId === vs.id) {
      return;
    }
    const updatedTemplate = _.cloneDeep(template);
    updatedTemplate.serviceName = vs.name;
    updatedTemplate.serviceId = vs.id;
    this.props.updateMockServiceTemplate(updatedTemplate);
  };

  saveTemplate = (rowData: MockServiceTemplate) => {
    const mockServiceTemplate = { ...rowData };
    if (_.isEmpty(mockServiceTemplate.thinkTime)) {
      mockServiceTemplate.thinkTime = 0;
    }
    this.props.saveMockServiceTemplate(mockServiceTemplate);
  };

  componentDidUpdate(): void {
    if (this.props.mockServiceTemplateList.length > 0) {
      const newMs = this.props.mockServiceTemplateList.find((ms: MockServiceTemplate) => ms.isNew);
      if (newMs) {
        this.toggleExpandDetail(newMs.id);
        this.goToFirstPage();
      }
    }
  }

  updateTemplate = (template: MockServiceTemplate) => {
    this.props.updateMockServiceTemplate(template);
  };

  updateTemplateTransaction = (template: MockServiceTemplate, transaction: Transaction) => {
    const updatedTxns: any = _.map(template.mockServiceTransactions, txn => {
      if (txn.txnId === transaction.id) {
        txn.priority = transaction.priority;
      }
      return txn;
    });

    template.mockServiceTransactions = updatedTxns;
    this.props.updateMockServiceTemplate(template);
  };

  nameChanged = _.debounce((rowData: MockServiceTemplate, nameValue: string) => {
    rowData.name = nameValue;
    this.props.updateMockServiceTemplate(rowData);
  }, 600);

  deleteMockTemplate = (rowData: MockServiceTemplate) => {
    const { t } = this.props;
    const modalBody = (
      <p>
        {t('mockServiceTemplatesTable.deleteConfirmation')} <b className="text-break">{rowData.name}</b>?
      </p>
    );
    confirm({
      confirmation: modalBody,
      title: t('mockServiceTemplatesTable.deleteMockServiceTemplate'),
      yesOptionTitle: t('common:actions.delete'),
    })
      .then(() => {
        this.props.deleteMockServiceTemplate(rowData);
      })
      .catch(error => {
        //nothing
        console.error(error);
      });
  };

  createMockServiceFromTemplate = (template: MockServiceTemplate) => {
    this.props.createMockServiceFromTemplate({ ...template });
  };

  /**
   * Updates the local selection state when the user checks/unchecks rows.
   * @param selected - currently selected template rows
   */
  onSelectionChange = (selected: MockServiceTemplate[]) => {
    this.setState({ selectedTemplates: selected });
  };

  /**
   * Shows a confirmation modal then dispatches the bulk delete thunk for selected templates.
   */
  onBulkDeleteClicked = () => {
    const { t } = this.props;
    const { selectedTemplates } = this.state;
    const isSingle = selectedTemplates.length === 1;
    const modalBody = (
      <p>
        {t('marFiles:table.bulkDeleteWarn')}{' '}
        <strong className="text-break">{selectedTemplates.length}</strong>{' '}
        {isSingle
          ? t('mockServiceTemplatesTable.bulkDeleteSingleTemplate')
          : t('mockServiceTemplatesTable.bulkDeleteMultipleTemplates')}
        <br />
        {t('mockServicesTable.cannotUndo')}
      </p>
    );
    confirm({
      confirmation: modalBody,
      title: t('mockServiceTemplatesTable.deleteSelectedTemplates'),
      yesOptionTitle: t('common:actions.delete'),
    })
      .then(() => {
        this.props.bulkDeleteTemplates(selectedTemplates);
        this.setState({ selectedTemplates: [] });
      })
      .catch(() => {
        // user dismissed modal
      });
  };

  RightSideButtonsCell = (rowData: any) => {
    const isInProgress = this.isServiceCreationInProgress(rowData.id);
    const { t } = this.props;
    return (
      <div>
        {!isInProgress && (
          <Button
            className="mr-3"
            variant="secondary"
            id={'qa-btn-template-create-mock-service-' + rowData.id}
            onClick={() => this.createMockServiceFromTemplate(rowData)}
            title={t('mockServiceTemplatesTable.createMockServiceFromTemplate')}
            disabled={this.props.isPrivateCloudReadOnly}
          >
            Create Virtual Service
          </Button>
        )}

        {isInProgress && (
          <div className="text-center pr-5" style={{ maxWidth: '140px', display: 'inline-block', lineHeight: '30px' }}>
            <SimpleLoader />
          </div>
        )}

        <BlazeTableActions>
          <button
            className="btn btn-link-dark btn-no-hover"
            id="deleteButId"
            disabled={this.props.isPrivateCloudReadOnly}
            onClick={() => this.deleteMockTemplate(rowData)}
          >
            <BzmIcon variant={'icon-bzm-delete'} className={'icon-xs'} />
          </button>
        </BlazeTableActions>
      </div>
    );
  };

  NameCellComponent = (rowData: MockServiceTemplate) => {
    const { t } = this.props;
    const isExpanded = this.isTemplateExpanded(rowData);
    const draftIcon = (
      <DraftIcon title="This Virtual Service Template contains local changes. Save or update to make changes available to others." />
    );

    return (
      <>
        <div className={styles.invalidName + 'py-1'} style={{ width: '100%' }}>
          {rowData.isDraft === true && <div className="text-center d-inline-block mr-3">{draftIcon}</div>}
          <div
            className="d-inline-block"
            style={{ width: !isExpanded ? undefined : rowData.isDraft === true ? '94%' : '100%' }}
          >
            {isExpanded ? (
              <BzFormControl
                disabled={this.props.isPrivateCloudReadOnly}
                defaultValue={rowData.name}
                required={true}
                onChange={(event: any) => this.nameChanged(rowData, event.target.value)}
                id="templateNewNameInput"
                className="form-control"
                dirty={true}
                invalidMessage={t('mockServiceTemplatesTable.mockServiceTemplateNameRequired')}
              />
            ) : (
              <ExpandLink
                value={rowData.name}
                id={rowData.id}
                onToggleExpandDetail={() => {
                  this.toggleExpandDetail(rowData.id);
                }}
              />
            )}
          </div>
        </div>
      </>
    );
  };

  toggleExpandDetail = (serviceTemplateId: number) => {
    if (this.getTable()) {
      this.getTable().expandRowHandler(serviceTemplateId);
    }
  };

  /**
   * Adds new blank template to the list and opens it in the table.
   */
  addNewTemplateHandler = () => {
    // Show in progress toast for a while
    const toast: IToast = this.props.showToastNotification(
      ToastType.Info,
      'Create Virtual Service template',
      'Creating new Virtual Service Template, please wait...',
      0,
    );

    this.props
      .createBlankMockServiceTemplate()
      .then((template: MockServiceTemplate) => {
        // Once finished, expand the created template row by id and paginate there
        this.getTable().expandRowHandler(template.id);
        this.getTable().goToPageWithSelectedItem(template.id);
      })
      .finally(() => {
        this.props.hideToast(toast);
      });
  };

  /**
   * Expand table item action.
   */
  expandTemplateItem = (templateId: number | null) => {
    //Reset detail view to transactions
    this.props.resetTemplateDetailView();
    this.props.expandTemplate(templateId);
  };

  /**
   * Checks whether service creation from template is in progress.
   * @param serviceTemplateId
   */
  isServiceCreationInProgress = (serviceTemplateId: number) => {
    return this.props.mockTemplateTableMeta.servicesInProgress.includes(serviceTemplateId);
  };

  ServiceCellComponent = (rowData: MockServiceTemplate) => {
    const isReadOnly =
      !this.isTemplateExpanded(rowData) ||
      rowData.isDraft === false ||
      (rowData.isDraft === true && rowData.serviceMockTemplateId !== undefined);
    return (
      <div style={{ zIndex: 3 }}>
        {/* Change of the service is possible only when it is brand-new template*/}
        {!isReadOnly ? (
          <ServicePicker
            isCreateEnabled={true}
            isSelectAll={false}
            preSelected={rowData.serviceId}
            manualPreLoad={true}
            selectionDisabled={this.props.isPrivateCloudReadOnly || rowData.mockServiceTransactions.length !== 0}
            selectHandler={vs => this.serviceChanged(rowData, vs)}
          />
        ) : (
          <div title={rowData.serviceName} className="text-truncate" style={{ overflow: 'hidden' }}>
            <span>{rowData.serviceName}</span>
          </div>
        )}
      </div>
    );
  };

  isTemplateExpanded = (template: MockServiceTemplate) => {
    return this.props.expandedTemplateId === template.id;
  };

  ExpandDetailComponent = (template: MockServiceTemplate) => {
    return (
      <div className="p-4">
        <MockTemplateDetail
          key={template.id}
          saveTemplateHandler={() => this.saveTemplate(template)}
          updateTemplateHandler={this.updateTemplate}
          updateTransactionHandler={(transaction: Transaction) => this.updateTemplateTransaction(template, transaction)}
          template={template}
          isSaving={this.props.isSaving}
          isReadOnly={this.props.isPrivateCloudReadOnly}
          invalid={false}
          isContainerScalable={true}
        />
      </div>
    );
  };

  // Definition of table columns
  colsDefinition: BlazeTableColDef[] = [
    {
      header: this.props.t('mockServiceTemplatesTable.columnNames.mockTemplateName'),
      cellComponent: (rowData: any) => this.NameCellComponent(rowData),
      accessor: 'name',
      cellClass: this.isTemplateExpanded ? 'undefined' : 'flex-table-col-overflow-visible',
    },
    {
      header: this.props.t('mockServiceTemplatesTable.columnNames.service'),
      headerComponent: (
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'relative', bottom: '3px' }}>
            {this.props.t('mockServiceTemplatesTable.columnNames.service')}
          </span>
          <span id="serviceHelp" className={styles.questionInTableHeader} title={this.props.t('common:showHelp')}>
            <HelpTooltip id={'Service_MockService_table'} />
          </span>
        </div>
      ),
      cellComponent: (rowData: any) => this.ServiceCellComponent(rowData),
      cellClass: 'flex-table-col-overflow-visible',
      accessor: 'service',
    },
    {
      header: this.props.t('mockServiceTemplatesTable.columnNames.actions'),
      // Custom cell component with access to rowData
      cellComponent: (rowData: any) => this.RightSideButtonsCell(rowData),
      accessor: 'serviceId',
      isSortable: false,
      cellClass: 'flex-table-col-overflow-visible',
      width: 200,
    },
  ];

  tableOptions: BlazeTableOptions = {
    expandDetailComponent: (rowData: any) => this.ExpandDetailComponent(rowData),
    showRowExpander: true,
    columnDef: this.colsDefinition,
    pageable: true,
    selectable: true,
    sortable: true,
    pageSize: 10,
    rowPadding: 8,
    tableName: 'mockServiceTemplates',
    noResultsText: this.props.t('mockServiceTemplatesTable.noMockServiceAvailable'),
    minWidth: 750,
    disabled: this.props.isPrivateCloudReadOnly,
  };

  createAddNewTemplateDom = () => {
    const { t } = this.props;
    return (
      <div className={'float-right'}>
        <button
          disabled={this.props.isPrivateCloudReadOnly}
          title={t('mockServiceTemplatesTable.createNew')}
          onClick={() => this.addNewTemplateHandler()}
          id="createServiceMockButton"
          className={`btn btn-primary btn-circular btn-jb ${styles.addMockServiceIcon}`}
        >
          <BzmIcon variant={'icon-bzm-add'} />
        </button>
      </div>
    );
  };

  _getFilteredResults = () => {
    let filteredTemplates = _.cloneDeep(this.props.mockServiceTemplateList);
    if (!_.isEmpty(this.props.searchTerm)) {
      filteredTemplates = applySearchTermFilterToList(filteredTemplates, this.props.searchTerm);
    }
    return filteredTemplates;
  };

  render() {
    const { t } = this.props;
    const { selectedTemplates } = this.state;
    const filteredTemplates = this._getFilteredResults();
    const headerText = getSelectedCountTitle(filteredTemplates.length, selectedTemplates.length);
    return (
      <>
        <div id="serviceMockTemplatesTitle" className="mb-4">
          {/* Add New Template*/}
          {this.createAddNewTemplateDom()}
          {/* ./ Add New Template*/}

          <h2 className={styles.title + ' mb-2'}>
            {t('mockServiceTemplatesTable.title')} {headerText}
            <ElevioTooltip elevioId={'MockServicesTemplates_heading_help'}></ElevioTooltip>
            <button
              id="qa-blk-tmpl-delete"
              className="btn btn-link btn-no-hover p-0 mx-2"
              title={t('mockServiceTemplatesTable.deleteSelectedTemplates')}
              onClick={this.onBulkDeleteClicked}
              disabled={selectedTemplates.length === 0 || this.props.isPrivateCloudReadOnly}
            >
              <BzmIcon variant={'icon-bzm-delete'} className={'icon-sm'} />
            </button>
          </h2>
          <p id="serviceMockVerbiage">{t('mockServiceTemplatesTable.description')}</p>
        </div>

        <BlazeTable
          data={filteredTemplates}
          options={this.tableOptions}
          isLoading={this.props.isLoading}
          expandedItem={this.props.expandedTemplateId}
          onExpandedItemChange={this.expandTemplateItem}
          onRef={(ref: any) => (this.tableRef = ref)}
          onSelectionChange={this.onSelectionChange}
          resolveRowKey={(item: MockServiceTemplate): number =>
            item.serviceMockTemplateId !== undefined ? item.serviceMockTemplateId : item.id
          }
        />
      </>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => ({
  mockServiceTemplateList: state.mockServiceTemplates.mockServiceTemplates,
  isLoading: state.mockServiceTemplates.isLoading,
  mockTemplateTableMeta: state.mockServiceTemplates.mockTemplateTableMeta,
  expandedTemplateId: state.mockServiceTemplates.expandedTemplateId,
  services: state.services.services,
  searchTerm: state.mockServiceTemplates.filter.search,
  activePage: state.mockServiceTemplates.activePage,
  isSaving: state.mockServiceTemplates.isSaving,
  isPrivateCloudReadOnly: state.blazeUserConfig.isPrivateCloudReadOnly,
  blazeUserConfig: state.blazeUserConfig.blazeUserConfig,
  activeDetailTab: state.mockServiceTemplateDetail.activeTab,
});

// Object Shorthand Form of mapDispatchToProps
// cannot be used because dispatch is swallowed by redux
const mapDispatchToProps = (dispatch: any) => ({
  // Get virtual service templates action
  getMockServiceTemplates: (
    serviceId?: number,
    forceRefresh?: boolean,
    backgroundLoading?: boolean,
    isNew?: boolean,
    templateId?: number,
  ) => dispatch(getMockServiceTemplates(serviceId, forceRefresh, backgroundLoading, isNew, templateId)),

  updateMockServiceTemplate: (template: MockServiceTemplate) => dispatch(updateMockServiceTemplate(template)),
  deleteMockServiceTemplate: (template: MockServiceTemplate) => dispatch(deleteMockServiceTemplate(template)),
  saveMockServiceTemplate: (mockServiceTemplate: MockServiceTemplate) =>
    dispatch(saveMockServiceTemplate(mockServiceTemplate)),
  createMockServiceFromTemplate: (template: MockServiceTemplate) => dispatch(createMockServiceFromTemplate(template)),
  createBlankMockServiceTemplate: () => dispatch(createBlankMockServiceTemplate()),
  expandTemplate: (templateId: number | null) => dispatch(expandTemplate(templateId)),
  showToastNotification: (type: ToastType, heading: string, content?: any, messageTimeout?: number) =>
    dispatch(showToastNotification(type, heading, content, messageTimeout)),
  hideToast: (toast: IToast) => dispatch(hideToast(toast)),
  fetchServices: (forceRefresh: boolean) => dispatch(fetchServices(forceRefresh)),
  getConfigurationsListAction: () => dispatch(getConfigurationsListAction()),
  resetTemplateDetailView: () => dispatch(resetTemplateDetailView()),
  bulkDeleteTemplates: (items: MockServiceTemplate[]) => dispatch(bulkDeleteTemplates(items)),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = {
  getMockServiceTemplates: typeof getMockServiceTemplates;
  updateMockServiceTemplate: typeof updateMockServiceTemplate;
  deleteMockServiceTemplate: typeof deleteMockServiceTemplate;
  saveMockServiceTemplate: typeof saveMockServiceTemplate;
  createMockServiceFromTemplate: typeof createMockServiceFromTemplate;
  showToastNotification: typeof showToastNotification;
  hideToast: typeof hideToast;
  expandTemplate: typeof expandTemplate;
  createBlankMockServiceTemplate: () => Promise<MockServiceTemplate>;
  fetchServices: (forceRefresh: boolean) => Promise<void>;
  getConfigurationsListAction: () => Promise<void>;
  resetTemplateDetailView: typeof resetTemplateDetailView;
  bulkDeleteTemplates: (items: MockServiceTemplate[]) => void;
};

export default withTranslation('mockServices')(
  connect(mapStateToProps, mapDispatchToProps)(MockTemplatesListBlazeTable),
);
